import React, { useState, useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import FieldSet from './FieldSet'
import FormControlFactory from './FormControlFactory'
import AccordionFieldSet from './AccordionFieldSet'
import FieldLockGroupRow from './FieldLockGroupRow'
import { IFieldSetSettings, FormStatus } from '../types/IFormSettings'
import {
	fieldSetSelector,
	formKeySelector,
} from '../selectors/formBuilder.selector'
import { FieldLockModeContext } from '../context/FieldLockModeContext'
import { FormLockModeContext } from '../context/FormLockModeContext'

function FieldSetBuilder({
	formName,
	controls,
	fieldSetName,
	visible,
	disabled,
	description,
	descriptionSubtext,
	tooltip,
	collapsable,
	iconSrc,
}: IFieldSetSettings) {
	const fieldSet = useSelector(fieldSetSelector(formName, fieldSetName))
	const isVisible = fieldSet && fieldSet.visible
	const fieldLockMode = useContext(FieldLockModeContext)
	const formLockContext = useContext(FormLockModeContext)
	const [unlockedGroups, setUnlockedGroups] = useState<Set<string>>(new Set())
	const formStatus = useSelector(formKeySelector(formName, 'formStatus'))
	const controlsState = useSelector(
		formKeySelector(formName, 'controlsState'),
	)

	const unlock = (id: string) =>
		setUnlockedGroups((prev) => {
			const s = new Set(prev)
			s.add(id)
			return s
		})
	const lock = (id: string) =>
		setUnlockedGroups((prev) => {
			const s = new Set(prev)
			s.delete(id)
			return s
		})

	// When a validation run finds errors, auto-unlock groups that contain invalid controls
	useEffect(() => {
		if (!fieldLockMode || formStatus !== FormStatus.notValid) return
		const seen = new Set<string>()
		controls.forEach((control) => {
			if (!control.viewGroup?.isPrimary) return
			const groupId = control.viewGroup.id
			if (seen.has(groupId)) return
			seen.add(groupId)
			const hasInvalid = controls
				.filter((c) => c.viewGroup?.id === groupId)
				.some((c) => controlsState?.[c.controlName]?.isValid === false)
			if (hasInvalid) unlock(groupId)
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formStatus])

	let renderFormControl: JSX.Element[]

	if (fieldLockMode || (formLockContext.lockMode && formLockContext.locked)) {
		const renderedGroupIds = new Set<string>()

		renderFormControl = controls.flatMap((control): JSX.Element[] => {
			if (!control.viewGroup) {
				return [
					<FormControlFactory
						formName={formName}
						key={control.controlName}
						controlName={control.controlName}
						type={control.type}
					/>,
				]
			}

			const { id, isPrimary, visible } = control.viewGroup

			// Non-primary group members are rendered as part of the primary's block
			if (!isPrimary || renderedGroupIds.has(id)) return []
			renderedGroupIds.add(id)

			if (visible === false) return []

			const groupControls = controls.filter((c) => c.viewGroup?.id === id)
			const isLocked =
				!unlockedGroups.has(id) ||
				(formLockContext.lockMode && formLockContext.locked)
			const {
				label = '',
				displayFields = [],
				formatValue,
			} = control.viewGroup
			const hasRequired = groupControls.some((c) => c.required)

			if (isLocked) {
				return [
					<FieldLockGroupRow
						key={`group-${id}`}
						formName={formName}
						label={label}
						displayFields={displayFields}
						formatValue={formatValue}
						col={control.col ?? 12}
						hasRequired={hasRequired}
						onUnlock={() => unlock(id)}
					/>,
				]
			}

			// Edit mode: label + checkbox above the edit box, fields inside
			return [
				<div
					key={`group-edit-${id}`}
					className={`form-box col-${control.col ?? 12}`}
					style={{ paddingLeft: 0, paddingRight: 0 }}>
					<div className="field-lock-group-edit">
						<div className="field-lock-group-label-row">
							<span className="field-lock-group-label">
								{label}
								{hasRequired && (
									<span className="required">*</span>
								)}
							</span>
							<input
								type="checkbox"
								className="field-lock-toggle"
								checked={false}
								onChange={() => lock(id)}
								aria-label={`Lock ${label}`}
							/>
						</div>
						<div className="field-lock-group-fields">
							<FieldLockModeContext.Provider value={false}>
								{groupControls.map((gc) => (
									<FormControlFactory
										key={gc.controlName}
										formName={formName}
										controlName={gc.controlName}
										type={gc.type}
									/>
								))}
							</FieldLockModeContext.Provider>
						</div>
					</div>
				</div>,
			]
		})
	} else {
		renderFormControl = controls.map((control) => (
			<FormControlFactory
				formName={formName}
				key={control.controlName}
				controlName={control.controlName}
				type={control.type}
			/>
		))
	}

	if (collapsable) {
		return (
			<AccordionFieldSet
				controls={controls}
				fieldSetName={fieldSetName}
				description={description}
				disabled={disabled}
				visible={visible}
				iconSrc={iconSrc}>
				{renderFormControl}
			</AccordionFieldSet>
		)
	}

	return isVisible === false ? (
		<></>
	) : (
		<FieldSet
			controls={controls}
			fieldSetName={fieldSetName}
			description={description}
			descriptionSubtext={descriptionSubtext}
			tooltip={tooltip}
			disabled={disabled}>
			{renderFormControl}
		</FieldSet>
	)
}

export default FieldSetBuilder
